import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import StripedDataGrid from 'shared/components/StripedDataGrid';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import { CompetitorsResponse, LossesResponse, LossRow } from '../types';
import {
  analysisPeriod,
  auctionInsightsUrl,
  campaignCompetitors,
  matchesCategories,
  CampaignCompetitor,
  CampaignHealth,
  FILTER_OPTIONS,
  FilterCategory,
} from '../analysis';

// The campaign manager's home base: one row per campaign answering "is my
// campaign losing impression share, to whom, and how bad — how much are we
// losing vs how much are they gaining?" Our-side loss diagnostics (budget/rank
// lost, $ at risk) come from /losses; competitor gains come from the campaign-
// level auction-insights rows — the same numbers the Slack alert fires on.

const money = (v: number | null | undefined): string =>
  v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
const pct = (v: number | null | undefined): string =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

// Comparison window: how many recent complete weeks count as "now" vs the
// preceding baseline. Wider windows smooth noise; narrower catch fresh moves.
const WINDOW_OPTIONS = [
  { label: 'Last 2 wks vs prior 4', current: 2, prior: 4 },
  { label: 'Last 1 wk vs prior 4', current: 1, prior: 4 },
  { label: 'Last 4 wks vs prior 4', current: 4, prior: 4 },
  { label: 'Last 6 wks vs prior 6', current: 6, prior: 6 },
];

interface HealthRow extends CampaignHealth {
  id: string;
  store: string;
  budgetLost: number | null;
  rankLost: number | null;
  atRisk: number | null;
  topRival: CampaignCompetitor | null;
  rivalExposure: number;
  problem: number; // max($ we lose, $ they gain) — the sort key
}

export default function CampaignHealthTab({ service }: { service: AuctionIntelligenceService }) {
  const [comp, setComp] = useState<CompetitorsResponse | null>(null);
  const [losses, setLosses] = useState<LossesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [typeFilter, setTypeFilter] = useState<FilterCategory[]>([]);
  const [campaignFilter, setCampaignFilter] = useState<string[]>([]);
  const [windowIdx, setWindowIdx] = useState(0);
  const [drill, setDrill] = useState<HealthRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      service.getCompetitors({ weeks: 12 }),
      service.getLosses({ level: 'campaign', days: 14, minSpend: 0 }),
    ])
      .then(([c, l]) => {
        if (cancelled) return;
        setComp(c);
        setLosses(l);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [service]);

  const period = useMemo(() => (comp ? analysisPeriod(comp.rows) : null), [comp]);

  // All campaigns, before the toolbar filters — also the source for the
  // campaign-picker options.
  const allRows = useMemo<HealthRow[]>(() => {
    if (!comp) return [];
    const lossByCampaign = new Map<string, LossRow>();
    for (const l of losses?.rows ?? []) lossByCampaign.set(String(l.campaign_id), l);

    const w = WINDOW_OPTIONS[windowIdx];
    return campaignCompetitors(comp.rows, comp.entitySpend, undefined, w.current, w.prior)
      .map((h): HealthRow => {
        const loss = h.campaignId ? lossByCampaign.get(h.campaignId) : undefined;
        const topRival = h.competitors.find(c => c.exposureWeekly > 0) ?? null;
        const rivalExposure = topRival?.exposureWeekly ?? 0;
        const atRisk = loss?.estimated_weekly_at_risk ?? null;
        return {
          ...h,
          id: `${h.campaignId ?? h.campaignName}`,
          store: loss?.store ?? '—',
          budgetLost: loss?.campaign_budget_lost ?? null,
          rankLost: loss?.campaign_rank_lost ?? null,
          atRisk,
          topRival,
          rivalExposure,
          problem: Math.max(atRisk ?? 0, rivalExposure),
        };
      })
      .sort((a, b) => b.problem - a.problem);
  }, [comp, losses, windowIdx]);

  const rows = useMemo<HealthRow[]>(() => {
    const wanted = new Set(campaignFilter);
    return allRows.filter(r => {
      if (wanted.size > 0 && !wanted.has(r.id)) return false;
      // A campaign matches the type filter if any gaining rival is of that type.
      if (typeFilter.length > 0 && !r.competitors.some(c => c.exposureWeekly > 0 && matchesCategories(c, typeFilter))) {
        return false;
      }
      if (onlyProblems && !(r.problem > 0 || (r.ourDeltaPp ?? 0) < -2)) return false;
      return true;
    });
  }, [allRows, campaignFilter, typeFilter, onlyProblems]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'campaignName',
        headerName: 'Campaign',
        flex: 2,
        minWidth: 240,
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.row.store !== '—' && <Chip size="small" label={p.row.store} sx={{ mr: 0.75 }} />}
            {p.row.campaignName}
          </span>
        ),
      },
      {
        field: 'weeklySpend',
        headerName: 'Spend/wk',
        width: 88,
        type: 'number',
        valueFormatter: ({ value }) => money(Number(value)),
      },
      {
        // Our impression share now — "how present am I?" (sortable).
        field: 'ourCurrentShare',
        headerName: 'Our IS',
        width: 80,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        // The change in our share — "am I losing?" (sortable, negative = loss).
        field: 'ourDeltaPp',
        headerName: 'Δ IS',
        width: 85,
        type: 'number',
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => {
          const d = p.row.ourDeltaPp;
          if (d == null) return '—';
          return (
            <span style={{ color: d <= -5 ? '#c62828' : d < 0 ? '#e65100' : d > 0 ? '#2e7d32' : '#9e9e9e', fontWeight: Math.abs(d) >= 5 ? 700 : 400 }}>
              {d > 0 ? '+' : ''}
              {d}pp
            </span>
          );
        },
      },
      {
        // Why we're losing: the dominant reason from the IS-loss diagnostics.
        field: 'rankLost',
        headerName: 'Why losing',
        width: 90,
        sortable: false,
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => {
          const budget = p.row.budgetLost ?? 0;
          const rank = p.row.rankLost ?? 0;
          if (p.row.budgetLost == null && p.row.rankLost == null) return <span style={{ color: '#9e9e9e' }}>—</span>;
          const budgetLed = budget >= rank;
          return (
            <span title={`Lost to budget ${pct(p.row.budgetLost)} · lost to rank ${pct(p.row.rankLost)}`}>
              {budgetLed ? 'budget ' : 'rank '}
              <b>{pct(budgetLed ? p.row.budgetLost : p.row.rankLost)}</b>
            </span>
          );
        },
      },
      {
        // How bad for us: only a real loss (positive at-risk) is shown.
        field: 'atRisk',
        headerName: 'We lose $/wk',
        width: 88,
        type: 'number',
        valueGetter: ({ row }) => Math.max(0, (row as HealthRow).atRisk ?? 0),
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => {
          const v = Math.max(0, p.row.atRisk ?? 0);
          if (v <= 0) return <span style={{ color: '#9e9e9e' }}>—</span>;
          return <span style={{ color: v >= 1000 ? '#c62828' : undefined, fontWeight: v >= 1000 ? 700 : 400 }}>{money(v)}</span>;
        },
      },
      {
        // To whom, and how much they gain — merged so both read at a glance.
        field: 'rivalExposure',
        headerName: 'Top rival gaining (their $/wk)',
        flex: 1.4,
        minWidth: 265,
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => {
          const r = p.row.topRival;
          if (!r) return <span style={{ color: '#9e9e9e' }}>none</span>;
          // Flex row so the domain truncates but the Δ, $ and chips (the
          // signal) always stay visible instead of clipping off the right edge.
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, width: '100%' }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {r.domain}
              </Box>
              <b style={{ color: '#c62828', flexShrink: 0 }}>+{r.deltaPp}pp</b>
              {p.row.rivalExposure > 0 && (
                <b style={{ color: p.row.rivalExposure >= 500 ? '#c62828' : undefined, flexShrink: 0 }}>
                  · {money(p.row.rivalExposure)}/wk
                </b>
              )}
              {r.isNewEntrant && <Chip label="new" color="error" size="small" sx={{ flexShrink: 0 }} />}
              {r.classification === 'our_expansion' && (
                <Chip label="ours" color="success" size="small" sx={{ flexShrink: 0 }} title="This campaign's spend ramped from ~zero, so the overlap is us entering their auctions — not an attack" />
              )}
            </Box>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 76,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<unknown, HealthRow>) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" title="All competitors in this campaign" onClick={() => setDrill(p.row)}>
              <GroupsIcon fontSize="small" />
            </IconButton>
            {p.row.campaignId && (
              <Link
                href={auctionInsightsUrl(comp?.accountId ?? '', p.row.campaignId, { ...(period ?? {}) })}
                target="_blank"
                rel="noreferrer"
                sx={{ fontSize: 12, alignSelf: 'center' }}
              >
                GAds
              </Link>
            )}
          </Stack>
        ),
      },
    ],
    [comp, period],
  );

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (!comp || allRows.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No campaign data yet — upload an auction-insights export (with campaign-level rows) first.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Your campaigns, worst problem first — are we losing impression share, to whom, and how much we
        lose vs. how much they gain ({WINDOW_OPTIONS[windowIdx].label.toLowerCase()}).
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Autocomplete
          multiple
          size="small"
          options={allRows}
          getOptionLabel={o => o.campaignName}
          value={allRows.filter(r => campaignFilter.includes(r.id))}
          onChange={(_e, val) => setCampaignFilter(val.map(v => v.id))}
          limitTags={2}
          sx={{ width: 340 }}
          renderInput={params => (
            <TextField {...params} label="Campaigns (yours)" placeholder={campaignFilter.length ? '' : 'All campaigns'} />
          )}
        />
        <TextField
          select
          size="small"
          label="Date range"
          value={windowIdx}
          onChange={e => setWindowIdx(Number(e.target.value))}
          sx={{ width: 190 }}
        >
          {WINDOW_OPTIONS.map((o, i) => (
            <MenuItem key={o.label} value={i}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel id="ch-type-filter" shrink>
            Rival type
          </InputLabel>
          <Select
            multiple
            displayEmpty
            labelId="ch-type-filter"
            input={<OutlinedInput notched label="Rival type" />}
            value={typeFilter}
            onChange={e =>
              setTypeFilter(
                typeof e.target.value === 'string'
                  ? (e.target.value.split(',') as FilterCategory[])
                  : (e.target.value as FilterCategory[]),
              )
            }
            renderValue={selected =>
              selected.length === 0
                ? 'Any'
                : FILTER_OPTIONS.filter(o => selected.includes(o.value)).map(o => o.label).join(', ')
            }
          >
            {FILTER_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={typeFilter.includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} />}
          label="Only campaigns needing attention"
        />
      </Stack>
      <Box sx={{ height: 640, width: '100%' }}>
        <StripedDataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableSelectionOnClick
          getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        />
      </Box>

      <Dialog open={drill != null} onClose={() => setDrill(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Competitors — {drill?.campaignName}
          <IconButton onClick={() => setDrill(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            Campaign-level impression share (Google-measured), last 2 complete weeks vs prior 4. Exposure = their
            share gain × our ~${Math.round(drill?.weeklySpend ?? 0).toLocaleString()}/wk campaign spend.
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Competitor</TableCell>
                <TableCell align="right">Their IS now</TableCell>
                <TableCell align="right">Δ share</TableCell>
                <TableCell align="right">$/wk exposed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(drill?.competitors ?? []).map(c => (
                <TableRow key={c.domain}>
                  <TableCell>
                    {c.domain}{' '}
                    {c.isNewEntrant && <Chip label="new entrant" color="error" size="small" />}
                    {c.classification === 'our_expansion' && (
                      <Chip label="our expansion" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">{pct(c.currentShare)}</TableCell>
                  <TableCell align="right" sx={{ color: c.deltaPp > 0 ? 'error.main' : c.deltaPp < 0 ? 'success.main' : undefined }}>
                    {c.deltaPp > 0 ? '+' : ''}
                    {c.deltaPp}pp
                  </TableCell>
                  <TableCell align="right">{c.exposureWeekly ? money(c.exposureWeekly) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
